import { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Icon } from "./Icon";
import type { RefObject } from "react";
import type { BookmarkData, ColorPalette } from "../types/narrative";
import { BookmarkTooltip } from "./BookmarkTooltip";
import { useUserInputContext } from "../hooks/useUserInputContext";

interface BookmarkButtonProps {
  onClick?: (snapshot: BookmarkData | null) => void;
  delay?: number;
  isBookmarked?: boolean;
  contentRef?: RefObject<HTMLDivElement | null>;
  sectionId?: string;
  colorPalette?: ColorPalette; // Section's color palette to store with bookmark
  isFirstBookmarkButton?: boolean; // True if this is the first bookmark button in the narrative
}

// Clone DOM content and prepare snapshot for bookmarking
function createSnapshot(
  contentRef: RefObject<HTMLDivElement | null>,
  sectionId: string,
  colorPalette?: ColorPalette,
): BookmarkData | null {
  if (!contentRef.current) return null;

  // Clone the content node
  const clone = contentRef.current.cloneNode(true) as HTMLElement;

  // Remove bookmark buttons from the clone (they have the bookmark-button class)
  const bookmarkButtons = clone.querySelectorAll(".bookmark-button");
  bookmarkButtons.forEach((btn) => btn.remove());

  // Remove any reaction buttons if present
  const reactionButtons = clone.querySelectorAll(".reaction-buttons");
  reactionButtons.forEach((btn) => btn.remove());

  return {
    snapshot: clone.outerHTML,
    sectionId,
    colorPalette, // Store the palette for consistent gradient rendering
  };
}

export function BookmarkButton({
  onClick,
  delay = 0,
  isBookmarked = false,
  contentRef,
  sectionId = "intro",
  colorPalette,
  isFirstBookmarkButton = false,
}: BookmarkButtonProps) {
  const { hasSeenBookmarkTooltip, setHasSeenBookmarkTooltip } =
    useUserInputContext();
  const [showTooltip, setShowTooltip] = useState(false);

  // Show tooltip only for the first bookmark button if user hasn't seen it
  useEffect(() => {
    if (isFirstBookmarkButton && !hasSeenBookmarkTooltip && !isBookmarked) {
      // Delay showing tooltip until button has animated in
      const timer = setTimeout(
        () => {
          setShowTooltip(true);
        },
        delay * 1000 + 600,
      ); // Wait for button animation + extra delay
      return () => clearTimeout(timer);
    }
  }, [isFirstBookmarkButton, hasSeenBookmarkTooltip, isBookmarked, delay]);

  const dismissTooltip = useCallback(() => {
    setShowTooltip(false);
    setHasSeenBookmarkTooltip();
  }, [setHasSeenBookmarkTooltip]);

  // Dismiss tooltip on any interaction (click, touch, scroll) anywhere in viewport
  useEffect(() => {
    if (!showTooltip) return;

    const handleAnyInteraction = () => {
      dismissTooltip();
    };

    // Use capture phase to catch events before they're handled by other elements
    document.addEventListener("click", handleAnyInteraction, { capture: true });
    document.addEventListener("touchstart", handleAnyInteraction, {
      capture: true,
    });
    document.addEventListener("scroll", handleAnyInteraction, {
      capture: true,
      passive: true,
    });
    document.addEventListener("wheel", handleAnyInteraction, {
      capture: true,
      passive: true,
    });

    return () => {
      document.removeEventListener("click", handleAnyInteraction, {
        capture: true,
      });
      document.removeEventListener("touchstart", handleAnyInteraction, {
        capture: true,
      });
      document.removeEventListener("scroll", handleAnyInteraction, {
        capture: true,
      });
      document.removeEventListener("wheel", handleAnyInteraction, {
        capture: true,
      });
    };
  }, [showTooltip, dismissTooltip]);

  const handleClick = () => {
    if (onClick) {
      if (isBookmarked) {
        // Unbookmarking - pass null
        onClick(null);
      } else if (contentRef) {
        // Bookmarking - delay snapshot to allow bar chart animations to complete
        // Charts typically animate for 2-3 seconds, add buffer
        setTimeout(() => {
          const snapshot = createSnapshot(contentRef, sectionId, colorPalette);
          onClick(snapshot);
        }, 500); // 500ms delay to capture final animation state
      } else {
        // No ref provided - pass null (shouldn't happen in practice)
        onClick(null);
      }
    }
  };

  return (
    <div className="relative">
      {/* Tooltip - only shown for first bookmark button */}
      <BookmarkTooltip visible={showTooltip} />

      <motion.button
        onClick={handleClick}
        className={`bookmark-button flex items-center gap-2 px-4 py-3 text-sm rounded-full font-medium shadow-lg cursor-pointer backdrop-blur-md border transition-colors ${
          isBookmarked
            ? "bg-emerald-500/20 hover:bg-emerald-500/10 text-emerald-300 border-emerald-400/30 hover:border-emerald-400/20"
            : "bg-white/10 hover:bg-white/20 text-white border-white/20"
        }`}
        initial={{ opacity: 0, y: 20, scale: 0.9 }}
        animate={{
          opacity: 1,
          y: 0,
          scale: 1,
        }}
        transition={{
          type: "spring",
          stiffness: 400,
          damping: 25,
          delay,
        }}
        whileHover={{ scale: 1.05, transition: { duration: 0.1 } }}
        whileTap={{ scale: 0.95, transition: { duration: 0.15 } }}
      >
        <AnimatePresence mode="wait">
          {isBookmarked ? (
            <motion.div
              key="bookmarked"
              className="flex items-center gap-2"
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.8 }}
              transition={{
                type: "spring",
                stiffness: 500,
                damping: 25,
              }}
            >
              <Icon icon="check" />
              <span>Bookmarked</span>
            </motion.div>
          ) : (
            <motion.div
              key="bookmark"
              className="flex items-center gap-2"
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.8 }}
            >
              <Icon icon="bookmark" />
              <span>Bookmark Insight</span>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.button>
    </div>
  );
}
